import * as React from 'react';
import {useState, useEffect} from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  BooleanInput,
  SelectInput,
  required,
  email,
  minLength,
  regex,
  useRecordContext,
  useResourceContext,
  useGetIdentity,
  Loading,
  useGetRecordId,
} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {CustomToolbar} from '../../components/customtoolbar/customToolbar.component';
import {Box, Grid, FormControlLabel, Switch} from '@mui/material';
import {normalizeEditUserPayload} from './user.edit.utils';

const editValidationKeys = {
  required: 'resources.users.views.edit.validation.required',
  emailInvalid: 'resources.users.views.edit.validation.emailInvalid',
  phoneInvalid: 'resources.users.views.edit.validation.phoneInvalid',
  passwordMinLength: 'resources.users.views.edit.validation.passwordMinLength',
};

const validatePhone = regex(/^((((0{2}?)|(\+){1})46)|0)7[\d]{8}/, editValidationKeys.phoneInvalid);

const generateGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const isSsoPassword = (password?: string) => Boolean(password?.startsWith('SSO-'));

const formatDateTime = (value?: string | Date) => {
  if (!value) {
    return '';
  }

  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(value));
};

const UserEditForm = ({isMe}: {isMe: boolean}) => {
  const [isSso, setIsSso] = useState(false);
  const [mustProvidePassword, setMustProvidePassword] = useState(false);
  const {setValue, resetField, trigger} = useFormContext();
  const record = useRecordContext();
  const isInitiallySso = isSsoPassword(record?.password);
  const requiresPasswordForSsoToggle = !isSso && mustProvidePassword;

  useEffect(() => {
    // Write both the SSO toggle state and a hidden sentinel so transform()
    // can know whether the record was originally SSO when the form was loaded.
    setValue('__isSso', isInitiallySso, {shouldDirty: false});
    setValue('__isInitiallySso', isInitiallySso, {shouldDirty: false});
    if (isInitiallySso) {
      setIsSso(true);
      setMustProvidePassword(true);
    }
  }, [isInitiallySso, setValue]);

  const handleSsoChange = (checked: boolean) => {
    setIsSso(checked);
    setValue('__isSso', checked, {shouldDirty: true});

    if (checked) {
      setMustProvidePassword(false);
      setValue('password', `SSO-${generateGuid()}`, {
        shouldDirty: true,
      });
      return;
    }

    setMustProvidePassword(true);
    resetField('password', {defaultValue: ''});
    setValue('password', '', {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    void trigger('password');
  };

  return (
    <>
      {!isMe && (
        <Box display="flex" mb={2}>
          <BooleanInput source="isActive" defaultValue={true} />
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isSso}
                  onChange={(e) => handleSsoChange(e.target.checked)}
                />
              }
              label="SSO"
              sx={{ marginLeft: '16px' }}
            />
            <Box sx={{ height: '20px' }}></Box>
          </Box>
        </Box>
      )}
      <Grid container width="100%" spacing={1}>
          <Grid item xs={8}>
            <Box display="flex">
              <Box flex={1} mr={1}>
                <TextInput source="firstName" fullWidth validate={[required(editValidationKeys.required)]} />
              </Box>
              <Box flex={1} ml={1} mr={1}>
                <TextInput source="lastName" fullWidth validate={[required(editValidationKeys.required)]} />
              </Box>
              <Box flex={1} ml={1}>
                {!isMe && (
                  <SelectInput
                    source="role"
                    fullWidth
                    validate={[required(editValidationKeys.required)]}
                    defaultValue={'user'}
                    choices={[
                      {id: 'admin', name: 'resources.users.fields.admin'},
                      {id: 'permitSupervisor', name: 'resources.users.fields.permitSupervisor'},
                      {id: 'productionOperator', name: 'resources.users.fields.productionOperator'},
                      {id: 'maintenanceTechnician', name: 'resources.users.fields.maintenanceTechnician'},
                    ]}
                  />
                )}
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={2} mr={1}>
                <TextInput
                  source="phone"
                  fullWidth
                  validate={[required(editValidationKeys.required), validatePhone]}
                />
              </Box>
              <Box flex={1}></Box>
            </Box>
          </Grid>

          <Grid item xs={8}>
            <Box display="flex">
              <Box flex={1} mr={1}>
                <TextInput
                  source="email"
                  fullWidth
                  validate={[required(editValidationKeys.required), email(editValidationKeys.emailInvalid)]}
                />
              </Box>
              <Box flex={1} ml={1}>
                <TextInput 
                  source="password" 
                  fullWidth 
                  type="password"
                  validate={
                    !isSso
                      ? requiresPasswordForSsoToggle
                        ? [
                            required(editValidationKeys.required),
                            minLength(6, editValidationKeys.passwordMinLength),
                          ]
                        : [minLength(6, editValidationKeys.passwordMinLength)]
                      : []
                  }
                  InputProps={{ readOnly: isSso }}
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: isSso ? 'action.disabledBackground' : 'background.paper',
                    },
                    '& .MuiInputBase-input': {
                      color: isSso ? 'text.disabled' : 'text.primary',
                      WebkitTextFillColor: isSso ? 'var(--mui-palette-text-disabled)' : 'inherit',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isSso ? 'rgba(0, 0, 0, 0.23)' : 'inherit',
                    },
                  }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={8}>
            <Box display="flex">
              <Box flex={1} mr={1}>
                <TextInput source="createdAt" disabled fullWidth format={formatDateTime} />
              </Box>
              <Box flex={1} ml={1}>
                <TextInput source="updatedAt" disabled fullWidth format={formatDateTime} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </>
    );
};

export const UserEdit = () => {
  const resource = useResourceContext();
  const recordId = useGetRecordId();
  const {data: identity} = useGetIdentity();

  if (!identity) {
    return <Loading />;
  }

  const isMe = identity.id === recordId;

  const transform = (data: any) => {
    // __isInitiallySso is written by UserEditForm on record load from the backend getOne response.
    const isInitiallySso = Boolean(data.__isInitiallySso);
    const {__isInitiallySso, ...rest} = data;
    return normalizeEditUserPayload(rest, {
      isInitiallySso,
      createSsoPassword: () => `SSO-${generateGuid()}`,
    });
  };

  return (
    <Edit
      resource={resource}
      id={recordId}
      queryOptions={{refetchOnMount: 'always'}}
      transform={transform}
      redirect="list"
      mutationMode="pessimistic"
      children={(
        <SimpleForm
        sanitizeEmptyValues={false}
        toolbar={<CustomToolbar noDelete={isMe} />}
        children={<UserEditForm isMe={isMe} />}
      />
      )}
    />
  );
};
